/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import twgIcon from '../../../../assets/twg.svg';
import waveIcon from '../../../../assets/wave.svg';
import { TWG_BRANDING } from '../../../../branding';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import type { SelectedMetric } from '../../../../utils/orgMetrics';
import { SpacingWrapper } from '../../SpacingWrapper';

import { AppIconsRow } from './assets/AppIconsRow';
import { AssetsIcon } from './assets/Asset';
import { PeopleIcon } from './assets/People';
import { RelationshipIcon } from './assets/Relationship';
import { messages } from './messages';

const styles = cssMap({
	container: {
		maxWidth: '100%',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'flex-start',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBlock: '120px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingInline: '88px',
		'@media (max-width: 30rem)': {
			paddingInline: token('space.0'),
		},
	},
	copyBlock: {
		maxWidth: '1200px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.600'),
	},
	line: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		rowGap: '0px',
		columnGap: token('space.100'),
	},
	text: {
		fontFamily: token('font.family.brand.heading'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: 1.5,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		letterSpacing: '0.5px',
		color: token('color.text.subtle'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '2.5rem',
		'@media (max-width: 48rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '32px',
		},
		'@media (max-width: 30rem)': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '24px',
		},
	},
	inlineIconContainer: {
		width: '40px',
		height: '40px',
		display: 'inline-flex',
		paddingInline: token('space.050'),
		paddingBlock: token('space.050'),
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		color: token('color.text.subtle'),
		marginRight: token('space.050'),
		'@media (max-width: 30rem)': {
			width: '32px',
			height: '32px',
		},
	},
	inlineIconContainerRotatedLeft: {
		transform: 'rotate(-4deg)',
	},
	inlineIconContainerRotatedRight: {
		transform: 'rotate(4deg)',
	},
	inlineIconContainerLeftPadded: {
		marginLeft: token('space.100'),
	},
	inlineIconAndText: {
		display: 'inline-flex',
		alignItems: 'center',
		verticalAlign: 'bottom',
	},
	inlineIcon: {
		width: '28px',
		height: '28px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
		borderRadius: '10px',
		'@media (max-width: 30rem)': {
			width: '24px',
			height: '24px',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
			borderRadius: '7px',
		},
	},
	appIcons: {
		marginBottom: token('space.050', '4px'),
		marginLeft: token('space.100'),

		// `:nth-of-type` over `:nth-child` because `@compiled/react`'s SSR
		// safety check warns about `:nth-child` (selector index can shift
		// between server and client render). Behaviour is identical here
		// since the only children of `.appIcons` are `<img>` elements.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'img:nth-of-type(n)': {
			transform: 'rotate(4deg)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'img:nth-of-type(2n)': {
			transform: 'rotate(0deg)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'img:nth-of-type(3n)': {
			transform: 'rotate(-6deg)',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		svg: {
			height: '40px',
			marginBottom: token('space.negative.100'),
		},
	},
	// favouriteAppIcon: {
	// 	width: '40px',
	// 	height: '40px',
	// 	// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
	// 	borderRadius: '10px',
	// 	'@media (max-width: 30rem)': {
	// 		width: '32px',
	// 		height: '32px',
	// 		// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
	// 		borderRadius: '7px',
	// 	},
	// },
});

type UserHighlightSectionProps = {
	stats?: readonly SelectedMetric[];
};

export function UserHighlightSection({ stats }: UserHighlightSectionProps): JSX.Element {
	const { identity } = useAtlassianUserContext();
	const { formatNumber } = useIntl();
	const itemsContributed = formatNumber(
		stats?.find((stat) => stat.id === 'totalNodes')?.value ?? 0,
		{
			notation: 'compact',
			compactDisplay: 'long',
			maximumFractionDigits: 2,
		},
	);
	const peopleCount = formatNumber(stats?.find((stat) => stat.id === 'totalUsers')?.value ?? 0);
	const relationshipsCount = formatNumber(
		stats?.find((stat) => stat.id === 'totalRelationships')?.value ?? 0,
		{
			notation: 'compact',
			compactDisplay: 'long',
			maximumFractionDigits: 2,
		},
	);
	const appCount = formatNumber(stats?.find((stat) => stat.id === 'totalApps')?.value ?? 0);
	// const figmaItemsCount = 10209;

	if (!stats?.length) {
		return (
			<SpacingWrapper>
				<section css={styles.container}>
					<div css={styles.copyBlock}>
						<div css={styles.line}>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.text}>
								{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
								<span css={styles.inlineIconContainer}>
									<img css={styles.inlineIcon} src={waveIcon} alt="" role="presentation" />
								</span>
								<FormattedMessage
									{...messages.noDataLine1}
									values={{
										name: identity.name,
									}}
								/>
							</p>
						</div>

						<div css={styles.line}>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<p css={styles.text}>
								<FormattedMessage
									{...messages.noDataLine2}
									values={{
										twg_branding: TWG_BRANDING,
										graphName: (chunk) => (
											<span css={styles.inlineIconAndText}>
												{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
												<span css={styles.inlineIconContainer}>
													<img css={styles.inlineIcon} src={twgIcon} alt="" role="presentation" />
												</span>
												{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
												<span>{chunk}</span>
											</span>
										),
									}}
								/>
							</p>
						</div>
					</div>
				</section>
			</SpacingWrapper>
		);
	}

	return (
		<SpacingWrapper>
			<section css={styles.container}>
				<div css={styles.copyBlock}>
					<div css={styles.line}>
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={styles.text}>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
							<span css={styles.inlineIconContainer}>
								<img css={styles.inlineIcon} src={waveIcon} alt="" role="presentation" />
							</span>
							<FormattedMessage
								{...messages.line1}
								values={{
									twg_branding: TWG_BRANDING,
									name: identity.name,
									graphName: (chunk) => (
										<span css={styles.inlineIconAndText}>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span css={styles.inlineIconContainer}>
												<img css={styles.inlineIcon} src={twgIcon} alt="" role="presentation" />
											</span>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span>{chunk}</span>
										</span>
									),
									itemsContributed: () => (
										<span css={styles.inlineIconAndText}>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span
												css={[
													styles.inlineIconContainer,
													styles.inlineIconContainerRotatedRight,
													styles.inlineIconContainerLeftPadded,
												]}
											>
												<AssetsIcon />
											</span>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span>{itemsContributed}</span>
										</span>
									),
									peopleCount: () => (
										<span css={styles.inlineIconAndText}>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span
												css={[
													styles.inlineIconContainer,
													styles.inlineIconContainerRotatedLeft,
													styles.inlineIconContainerLeftPadded,
												]}
											>
												<PeopleIcon />
											</span>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span>{peopleCount}</span>
										</span>
									),
									relationshipsCount: () => (
										<span css={styles.inlineIconAndText}>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span
												css={[
													styles.inlineIconContainer,
													styles.inlineIconContainerRotatedRight,
													styles.inlineIconContainerLeftPadded,
												]}
											>
												<RelationshipIcon />
											</span>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span>{relationshipsCount}</span>
										</span>
									),
									appCount: () => (
										<span>
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span css={styles.appIcons} aria-hidden>
												<AppIconsRow />
											</span>
											&nbsp;
											{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
											<span>{appCount}</span>
										</span>
									),
								}}
							/>
						</p>
					</div>

					{/* Disabling this line as we don't have all the data for this one yet. */}
					{/* <div css={styles.line}> */}
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					{/* <p css={styles.text}>
							<FormattedMessage
								{...messages.line2}
								values={{
									mostPopularApp: () => (
										<span css={styles.inlineIconAndText}>
											<span
												css={[styles.inlineIconContainer, styles.inlineIconContainerRotatedLeft]}
											>
												<img
													src={figmaIcon}
													css={styles.favouriteAppIcon}
													alt=""
													role="presentation"
												/>
											</span> */}
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					{/* <span>
												<FormattedMessage {...messages.mostPopularAppName} />
											</span>
										</span>
									),
									mostPopularAppContributions: formatNumber(figmaItemsCount),
									itemsContributed: itemsContributed,
								}}
							/>
						</p>
					</div> */}
				</div>
			</section>
		</SpacingWrapper>
	);
}
